/**
 * Spec 088c — AuthProvider + useAuth hook.
 *
 * This module owns the client-side identity of the current user. It fetches
 * `GET /api/v1/me` (owned by spec 088a — `src/routes/me.ts`) EXACTLY ONCE
 * per session and caches the result in React context. Every consumer
 * (ProtectedRoute, Sidebar, BrandProvider) reads from the same source of
 * truth — no duplicate requests, no race conditions.
 *
 * Role matching (Q2 in clarifications.md):
 *   - Literal match on `user.roles` is the primary rule.
 *   - For `orbit:*`-style roles, `hasRole('admin')` auto-prefixes and
 *     tests `'orbit:admin'`. This mirrors `OrbitAuth.requireRole` and
 *     keeps call sites concise.
 *   - GEO Observatory-namespaced roles (e.g. `rule_admin`) are matched literally
 *     only — no magic prefix.
 *
 * 401 handling: `apiFetch` already redirects to `/signin` on 401, so
 * this provider does not need additional error handling for unauth'd
 * users — the redirect happens before the promise rejects.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { apiGet } from './api.js';

/**
 * Shape returned by GET /api/v1/me (spec 088a FR-D1).
 *
 * Note: `tenantId` is deprecated per spec 076 — the server returns
 * `null` for backward compatibility; the frontend derives its tenant
 * from the selected brand.
 */
export interface AuthUser {
  email: string;
  name: string;
  picture: string | null;
  roles: string[];
  isAdmin: boolean;
  isAppOwner: boolean;
  isDeveloper: boolean;
  appAccess: string[];
}

export interface AuthContextValue {
  /** Current user, or null if not yet fetched / unauthenticated. */
  user: AuthUser | null;
  /**
   * Returns true if the user has the given role. Auto-prefixes `orbit:`
   * for convenience (mirrors `OrbitAuth.requireRole` pattern).
   */
  hasRole: (role: string) => boolean;
  /** True while the initial /api/v1/me fetch is in flight. */
  isLoading: boolean;
  /** Last error encountered fetching /api/v1/me (null on success). */
  error: string | null;
}

/**
 * Raw `/api/v1/me` response shape. Kept separate from `AuthUser` so we can
 * normalize `picture: undefined` → `picture: null` at the boundary.
 */
interface MeApiResponse {
  email: string;
  name: string;
  picture?: string | null;
  tenantId: string | null;
  roles: string[];
  isAdmin: boolean;
  isAppOwner: boolean;
  isDeveloper: boolean;
  appAccess: string[];
}

const AuthContext = createContext<AuthContextValue | null>(null);

function normalizeUser(raw: MeApiResponse): AuthUser {
  return {
    email: raw.email,
    name: raw.name,
    picture: raw.picture ?? null,
    roles: Array.isArray(raw.roles) ? raw.roles : [],
    isAdmin: Boolean(raw.isAdmin),
    isAppOwner: Boolean(raw.isAppOwner),
    isDeveloper: Boolean(raw.isDeveloper),
    appAccess: Array.isArray(raw.appAccess) ? raw.appAccess : [],
  };
}

/**
 * AuthProvider — fetches `/api/v1/me` once on mount and exposes the
 * resulting user (plus helpers) via context. Place this ABOVE
 * `BrandProvider` in the component tree (see App.tsx).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await apiGet<MeApiResponse>('/api/v1/me');
        if (cancelled) return;
        setUser(normalizeUser(raw));
      } catch (err) {
        if (cancelled) return;
        // 401 is handled inside apiFetch (redirect to /signin) and the
        // promise never resolves; any error reaching here is a different
        // class of failure (network, malformed JSON, etc.).
        setError(err instanceof Error ? err.message : 'Failed to load user');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const hasRole = (role: string): boolean => {
      if (!user) return false;
      if (user.roles.includes(role)) return true;
      // Mirror OrbitAuth.requireRole: auto-prefix `orbit:` for non-namespaced
      // inputs. Skip when the caller already passed a `orbit:` role.
      if (!role.startsWith('orbit:')) {
        const prefixed = `orbit:${role}`;
        if (user.roles.includes(prefixed)) return true;
      }
      return false;
    };
    return { user, hasRole, isLoading, error };
  }, [user, isLoading, error]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth — the only sanctioned way to read the current user in the SPA.
 *
 * Throws if called outside an `AuthProvider` — catches integration
 * mistakes at runtime rather than letting components silently see a
 * null user.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
